import { useState, useEffect, useRef } from "react";
import { getApiUrl } from "../utils/api";
import { FaCheckCircle, FaExclamationCircle, FaSpinner } from "react-icons/fa";

export default function OtpModal({ open, email, onClose, onVerified }) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  // Reset OTP when modal opens
  useEffect(() => {
    if (open) {
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setSuccess("");
      setResendMsg("");
      setCountdown(60);
      setCanResend(false);
      // Focus first input after a brief delay
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [open]);

  // Countdown timer for resend
  useEffect(() => {
    if (open && countdown > 0 && !canResend) {
      const timer = setTimeout(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open, countdown, canResend]);

  if (!open) return null;

  const handleVerify = async (otpValue = null) => {
    const otpString = otpValue || otp.join("");
    if (otpString.length !== 6) {
      setError("Please enter all 6 digits");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await fetch(getApiUrl("/api/auth/verify-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, otp: otpString }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(data.msg || "Email verified. Signing you in...");
        setTimeout(() => {
          setSuccess("");
          onVerified && onVerified(data);
          onClose && onClose();
        }, 900);
      } else {
        setError(data.error || data.msg || "Verification failed");
        // Clear OTP on error
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError("Network error. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newOtp.every((digit) => digit !== "") && newOtp.join("").length === 6) {
      handleVerify(newOtp.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, "").slice(0, 6).split("");
        const newOtp = [...otp];
        digits.forEach((digit, i) => {
          if (i < 6) newOtp[i] = digit;
        });
        setOtp(newOtp);
        if (digits.length === 6) {
          handleVerify(newOtp.join(""));
        } else {
          inputRefs.current[digits.length]?.focus();
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const digits = pastedText.replace(/\D/g, "").slice(0, 6).split("");
    const newOtp = [...otp];
    digits.forEach((digit, i) => {
      if (i < 6) newOtp[i] = digit;
    });
    setOtp(newOtp);
    if (digits.length === 6) {
      handleVerify(newOtp.join(""));
    } else {
      inputRefs.current[digits.length]?.focus();
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setResendLoading(true);
    setResendMsg("");
    setError("");
    setOtp(["", "", "", "", "", ""]);
    setCanResend(false);
    setCountdown(60);
    try {
      const res = await fetch(getApiUrl("/api/auth/resend-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setResendMsg("A new code has been sent to your email.");
        inputRefs.current[0]?.focus();
      } else {
        setError(data.error || data.msg || "Failed to resend OTP");
        setCanResend(true);
      }
    } catch (err) {
      setError("Network error. Please try again.");
      setCanResend(true);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl transition"
          aria-label="Close"
        >
          &times;
        </button>
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold mb-2 text-gray-800">
            Verify Your Email
          </h2>
          <p className="text-gray-600 text-sm">
            Enter the 6-digit code sent to{" "}
            <span className="font-semibold text-[#f26522]">{email}</span>
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleVerify();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex justify-center gap-2 mb-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:ring-2 focus:ring-[#f26522] focus:border-[#f26522] outline-none transition-all"
                style={{
                  borderColor: error
                    ? "#ef4444"
                    : digit
                    ? "#f26522"
                    : "#e5e7eb",
                }}
                disabled={loading}
              />
            ))}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
              <FaExclamationCircle />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded-lg">
              <FaCheckCircle />
              <span>{success}</span>
            </div>
          )}

          {resendMsg && (
            <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-3 rounded-lg">
              <FaCheckCircle />
              <span>{resendMsg}</span>
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-[#f26522] hover:opacity-90 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || otp.join("").length !== 6}
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              "Verify"
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={handleResend}
            className="text-sm text-[#f26522] hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition"
            disabled={resendLoading || !canResend}
          >
            {resendLoading ? (
              <span className="flex items-center justify-center gap-2">
                <FaSpinner className="animate-spin" />
                <span>Resending...</span>
              </span>
            ) : canResend ? (
              "Resend Code"
            ) : (
              `Resend code in ${countdown}s`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
